import React from "react";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import { useMobilePrefs } from "../ui/MobilePrefs";
import { SheetBody } from "../ui/SheetHost";
import { fmtAmtNum } from "@/lib/money";

/* CurrencySheet (design 1b) — tap a wallet to switch the active play currency;
   every stake/tile/slip re-tints. Real balances; PCash leads its own redeem rule.
   PCoins selected = soft green tint; the single SOLID-green fill is reserved for
   the PCash selected state per PREMIUM_UI_DIRECTION §4.3. Shows all three wallets:
   PCoins + PCash are selectable play currencies; Bonus is shown (informational,
   not selectable) only when the user holds a bonus balance — it can't be a play
   currency here (provider games + stakes use coins/cash, never bonus). Balances
   use the FULL precise fmtAmtNum (the sheet has room; the top-bar pill abbreviates). */

export default function CurrencySheet({ close, openCashier }) {
  const { user, streamerMode } = useAuth();
  const { playCurrency, setPlayCurrency } = useMobilePrefs();
  const mask = !!streamerMode;
  const bonusBal = Number(user?.bonus ?? 0);

  const wallets = [
    { id: "coins", name: "PCoins", sub: "Free play · no cash value", bal: fmtAmtNum(user?.coins ?? 0, "coins", { mask }), icon: "pnrcoin", color: "#00D632", selectable: true },
    { id: "cash", name: "PCash", sub: "Redeemable 1:1 · $50 min", bal: fmtAmtNum(user?.cash ?? 0, "cash", { mask }), icon: "pnrcash", color: "#E4E4E7", selectable: true },
    // Bonus — locked promotional PCash (1x playthrough). Display-only: it can't be
    // chosen as the play currency here, so it never shows a selected ring.
    ...(bonusBal > 0
      ? [{ id: "bonus", name: "Bonus", sub: "Locked · 1x playthrough", bal: fmtAmtNum(bonusBal, "bonus", { mask }), icon: "rewards", color: "#A1A1AA", selectable: false }]
      : []),
  ];

  const pick = (w) => { if (!w.selectable) return; setPlayCurrency(w.id); close(); };

  return (
    <SheetBody>
      <div style={{ font: "700 11px 'Outfit'", letterSpacing: ".16em", color: "#71717a", margin: "4px 0 12px" }}>PLAY WITH</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {wallets.map((w) => {
          const on = w.selectable && playCurrency === w.id; // bonus can't be selected
          const solid = on && w.id === "cash"; // solid green fill only for selected PCash
          return (
            <button
              key={w.id} type="button" onClick={() => pick(w)} aria-pressed={on}
              aria-disabled={!w.selectable} tabIndex={w.selectable ? 0 : -1}
              style={{
                display: "flex", alignItems: "center", gap: 12, padding: "14px 14px", borderRadius: 16, textAlign: "left", border: 0, cursor: w.selectable ? "pointer" : "default",
                background: solid ? "#00D632" : on ? "rgba(0,214,50,.07)" : "#141417",
                boxShadow: solid ? "inset 0 1px 0 rgba(255,255,255,.3)" : on ? "inset 0 0 0 1.5px #00D632" : "inset 0 0 0 1px #26262B",
                color: solid ? "#0A0A0A" : "#fff",
                opacity: w.selectable ? 1 : 0.82,
              }}
            >
              <Icon name={w.icon} size={26} style={{ color: solid ? "#0A0A0A" : w.color, flex: "none" }} />
              <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ font: "800 16px 'Manrope'" }}>{w.name}</span>
                <span style={{ font: "500 12px 'Outfit'", color: solid ? "rgba(10,10,10,.7)" : "#a1a1aa" }}>{w.sub}</span>
              </span>
              <span className="m-tnum" style={{ font: "800 18px 'Manrope'", whiteSpace: "nowrap" }}>{w.bal}</span>
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
        <button
          type="button" onClick={() => { close(); openCashier?.("get"); }}
          style={{ flex: 1, height: 48, borderRadius: 14, background: "#00D632", color: "#0A0A0A", font: "800 14px 'Manrope'", border: 0, cursor: "pointer", boxShadow: "inset 0 1px 0 rgba(255,255,255,.3)", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
        >
          <Icon name="plus" size={16} style={{ color: "#0A0A0A" }} /> Get Coins
        </button>
        <button
          type="button" onClick={() => { close(); openCashier?.("redeem"); }}
          style={{ flex: 1, height: 48, borderRadius: 14, background: "#141417", color: "#fff", font: "800 14px 'Manrope'", border: 0, cursor: "pointer", boxShadow: "inset 0 0 0 1px #2F2F35", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
        >
          <Icon name="upload" size={16} className="text-white" /> Redeem
        </button>
      </div>

      <p style={{ font: "500 11.5px/1.5 'Outfit'", color: "#71717a", marginTop: 14 }}>
        PCoins have no cash value. 1 PCash = $1, redeemable from $50 once verified.
      </p>
    </SheetBody>
  );
}
